import React, { Component } from "react";
import {
  Nav,
  Loader,
  Card,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  BarChart,
  TabsCustom,
  TabsCustomHeader,
  TabsCustomMenu,
  TabsCustomBody,
  TabsCustomItem,
} from "ui-kit-ck-consultant";
import FadeIn from "react-fade-in";
import { Doughnut } from "react-chartjs-2";
import "chart.js/auto";
import { Row, Col } from "react-flexbox-grid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload } from "@fortawesome/free-solid-svg-icons";

import ShowOptionsNGRA from "../../../components/general/ShowOptionsNGRA-v2";

import axiosConfig from "../../../utils/axiosConfig";
import { getCurrencyValue, getValue } from "../../../utils/general";

import AuthContext from "../../../context/AuthContext";
import filterStore from "../../../stores/filterStore";
import { colors } from "../../../utils/colors";

const CHART_COLORS = [
  colors.blue[0],
  colors.green[0],
  colors.orange[0],
  colors.purple[0],
  colors.red[0],
  colors.cyan[0],
  colors.yellow[0],
  colors.pink[0],
];

const getColor = (index) => CHART_COLORS[index % CHART_COLORS.length];

// Diamètre MAXIMUM du donut. On passe ici par react-chartjs-2 directement (et
// non par le PieChart de ui-kit) : ce dernier configure la légende, le tooltip
// et le cutout via des props Chart.js v2, sans effet sur le chart.js 3 du
// projet, et n'expose pas `radius`. Sans `radius`, le donut s'étire pour
// remplir la place laissée par la légende — donc énorme sur les camemberts à
// légende courte (région) et minuscule sur ceux à légende longue (agence).
const PIE_MAX_DIAMETER = 280;
// Encombrement de la légende dans une colonne md={4}, mesuré sur le rendu réel
// (89 agences tiennent en 16 lignes) : compter ~5 entrées par ligne. Surestimer
// le nombre de lignes ne coûte que du blanc ; le sous-estimer rogne la légende.
const PIE_LEGEND_ITEMS_PER_ROW = 5;
const PIE_LEGEND_ROW_HEIGHT = 20;

const GROUP_COLOR = "#1e1f26";

export default class CompanyImpaye extends Component {
  constructor(props) {
    super(props);
    const saved = filterStore.getState().getPageState("companyImpaye");
    this.state = saved || {
      isLoad: false,
      year: 0,
      network: 0,
      group: 0,
      region: 0,
      agency: 0,
      companyMultiples: [],
      impayeData: null,
      tabIndex: 0,
    };
  }

  static contextType = AuthContext;

  componentWillUnmount() {
    filterStore.getState().setPageState("companyImpaye", this.state);
  }

  handleDataChange = (data) => {
    filterStore.getState().setPageFilters("companyImpaye", data);
    this.setState(
      {
        year: data.year,
        network: data.network,
        group: data.group,
        region: data.region,
        agency: data.agency,
        companyMultiples: data.companyMultiples,
      },
      () => {
        this.getData();
      }
    );
  };

  getData = () => {
    this.setState({ isLoad: true }, () => {
      axiosConfig("/company-impaye/get-data", {
        data: {
          network: this.state.network,
          group: this.state.group,
          region: this.state.region,
          agency: this.state.agency,
          companyMultiples: this.state.companyMultiples,
        },
      })
        .then((res) => {
          if (res.data.success) {
            this.setState({ impayeData: res.data.data, isLoad: false });
          } else {
            this.setState({ isLoad: false, impayeData: null }, () => {
              alert("Une erreur est survenue");
            });
          }
        })
        .catch(() => {
          this.setState({ isLoad: false }, () => {
            alert("Une erreur est survenue");
          });
        });
    });
  };

  // Construit les dataSet d'un BarChart à partir d'un dictionnaire { nom: [valeurs] }.
  // colorMap (optionnel) = couleurs BDD par nom ; sinon palette.
  buildDatasets = (seriesByKey, colorMap = {}) => {
    return Object.keys(seriesByKey)
      .sort()
      .map((name, idx) => ({
        label: name,
        data: seriesByKey[name],
        type: "line",
        color: colorMap[name] || getColor(idx),
      }));
  };

  renderMultiChart = (title, labels, seriesByKey, format, digits, colorMap) => {
    if (!seriesByKey || !Object.keys(seriesByKey).length) return null;
    return (
      <Card title={title} className="mb-10 mb-20-xl">
        <BarChart
          digits={digits}
          height={320}
          isLegend={true}
          format={format}
          yAxes={[{ id: "A", ticks: { beginAtZero: true } }]}
          labels={labels}
          dataSet={this.buildDatasets(seriesByKey, colorMap)}
        />
      </Card>
    );
  };

  // Hauteur COMMUNE aux trois camemberts. Chart.js dessine la légende DANS le
  // canvas : la boîte doit donc contenir le donut ET toutes ses entrées de
  // légende, sinon Chart.js rogne la légende. Comme on affiche une part par
  // compagnie / agence / région (plus de regroupement "Autres"), on dimensionne
  // sur le camembert le plus fourni — l'agence — et on applique la même valeur
  // aux trois cartes pour qu'elles restent alignées.
  getPieHeight = (distributions) => {
    const maxEntries = distributions.reduce(
      (max, d) => Math.max(max, (d || []).length),
      0
    );
    const legendRows = Math.ceil(maxEntries / PIE_LEGEND_ITEMS_PER_ROW);
    return PIE_MAX_DIAMETER + legendRows * PIE_LEGEND_ROW_HEIGHT;
  };

  renderPie = (title, distribution, height) => {
    if (!distribution || !distribution.length) return null;
    return (
      <Col xs={12} md={4}>
        <Card title={title} className="mb-10 mb-20-xl">
          <div style={{ position: "relative", width: "100%", height }}>
            <Doughnut
              data={{
                labels: distribution.map((d) => d.labels),
                datasets: [
                  {
                    data: distribution.map((d) => d.data),
                    backgroundColor: distribution.map(
                      (d, idx) => d.color || getColor(idx)
                    ),
                    borderWidth: 0,
                  },
                ],
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                // Borne le donut : sans ça il absorbe toute la hauteur libre.
                radius: PIE_MAX_DIAMETER / 2,
                cutout: "70%",
                plugins: {
                  datalabels: { display: false },
                  legend: {
                    position: "bottom",
                    labels: {
                      usePointStyle: true,
                      boxWidth: 8,
                      padding: 8,
                      font: { size: 11 },
                    },
                  },
                  tooltip: {
                    callbacks: {
                      label: (ctx) =>
                        `${ctx.label} : ${getCurrencyValue(ctx.parsed)}`,
                    },
                  },
                },
              }}
            />
          </div>
        </Card>
      </Col>
    );
  };

  renderGroupChart = (title, labels, series, format, digits) => {
    if (!series) return null;
    return (
      <Card title={title} className="mb-10 mb-20-xl">
        <BarChart
          digits={digits}
          height={320}
          isLegend={false}
          format={format}
          yAxes={[{ id: "A", ticks: { beginAtZero: true } }]}
          labels={labels}
          dataSet={[
            { label: "Groupe", data: series, type: "line", color: GROUP_COLOR },
          ]}
        />
      </Card>
    );
  };

  buildAndDownloadCSV = (folders) => {
    if (!folders || !folders.length) return;
    const headers = [
      "Référence dossier",
      "N° facture",
      "Compagnie",
      "Région",
      "Agence",
      "Date facture",
      "Montant HT",
      "Montant TTC",
    ];
    const rows = folders.map((f) => [
      f.folderRef,
      f.invoice,
      f.company,
      f.region,
      f.agency,
      f.date,
      f.amountHt,
      f.amountTtc,
    ]);
    const csv = [headers, ...rows]
      .map((r) => r.map((v) => `"${v}"`).join(";"))
      .join("\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "dossiers_impayes.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Récupère la liste COMPLÈTE de l'encours (pas l'aperçu plafonné) pour l'export.
  downloadCSV = () => {
    this.setState({ isLoad: true }, () => {
      axiosConfig("/company-impaye/export-folders", {
        data: {
          network: this.state.network,
          group: this.state.group,
          region: this.state.region,
          agency: this.state.agency,
          companyMultiples: this.state.companyMultiples,
        },
      })
        .then((res) => {
          this.setState({ isLoad: false });
          if (res.data.success) {
            this.buildAndDownloadCSV(res.data.data.folders);
          } else {
            alert("Une erreur est survenue");
          }
        })
        .catch(() => {
          this.setState({ isLoad: false }, () => alert("Une erreur est survenue"));
        });
    });
  };

  render() {
    const data = this.state.impayeData;

    return (
      <>
        <FadeIn>
          <Nav
            className="mb-10 mb-20-xl nowrap"
            title="Impayés (factures de plus de 100 jours)"
          />
          <ShowOptionsNGRA
            isChange={true}
            className="mb-10 mb-20-xl"
            onDataChange={this.handleDataChange}
            initialFilters={filterStore
              .getState()
              .getPageFilters("companyImpaye")}
            isAll={true}
            isCompanyMultiples={true}
            visibilityRole={this.props.visibilityRole}
            visibilityType={this.props.visibilityType}
            preloadRole={this.props.preloadRole}
            preloadType={this.props.preloadType}
            limitUserType={this.props.limitUserType}
            limitUserRole={this.props.limitUserRole}
            allUserRole={this.props.allUserRole}
          />

          {data ? (
            <>
              <Row>
                <Col xs={12} md={3}>
                  <Card className="mb-10 mb-20-xl" title="Encours impayé à date">
                    <h2 className="text-center">
                      {getCurrencyValue(data.summary.totalAmount)}
                    </h2>
                  </Card>
                </Col>
                <Col xs={12} md={3}>
                  <Card className="mb-10 mb-20-xl" title="Dossiers impayés à date">
                    <h2 className="text-center">{data.summary.totalFolders}</h2>
                  </Card>
                </Col>
                <Col xs={12} md={3}>
                  <Card className="mb-10 mb-20-xl" title="Factures impayées">
                    <h2 className="text-center">{data.summary.totalInvoices}</h2>
                  </Card>
                </Col>
                <Col xs={12} md={3}>
                  <Card className="mb-10 mb-20-xl" title="Âge moyen à date">
                    <h2 className="text-center">
                      {getValue(data.summary.averageAge)} j
                    </h2>
                  </Card>
                </Col>
              </Row>

              <Row>
                {(() => {
                  const pieHeight = this.getPieHeight([
                    data.distributionByCompany,
                    data.distributionByAgency,
                    data.distributionByRegion,
                  ]);
                  return (
                    <>
                      {this.renderPie(
                        "Répartition de l'encours à date — par compagnie",
                        data.distributionByCompany,
                        pieHeight
                      )}
                      {this.renderPie(
                        "Répartition de l'encours à date — par agence",
                        data.distributionByAgency,
                        pieHeight
                      )}
                      {this.renderPie(
                        "Répartition de l'encours à date — par région",
                        data.distributionByRegion,
                        pieHeight
                      )}
                    </>
                  );
                })()}
              </Row>

              <TabsCustom>
                <TabsCustomHeader>
                  {["Compagnie", "Groupe", "Région", "Agence"].map(
                    (name, idx) => (
                      <TabsCustomMenu
                        key={idx}
                        title={name}
                        active={this.state.tabIndex === idx}
                        onClick={() => {
                          if (this.state.tabIndex !== idx) {
                            this.setState({ tabIndex: idx });
                          }
                        }}
                      />
                    )
                  )}
                </TabsCustomHeader>
                <TabsCustomBody noCard>
                  <TabsCustomItem active={this.state.tabIndex === 0}>
                    {this.state.tabIndex === 0 ? (
                      <>
                        {this.renderMultiChart(
                          "Encours impayé à fin de mois (€) — par compagnie",
                          data.labels,
                          data.amountByCompany,
                          "currency",
                          2,
                          data.companyColors
                        )}
                        {this.renderMultiChart(
                          "Dossiers impayés à fin de mois — par compagnie",
                          data.labels,
                          data.countByCompany,
                          null,
                          0,
                          data.companyColors
                        )}
                        {this.renderMultiChart(
                          "Âge moyen de l'encours (jours) — par compagnie",
                          data.labels,
                          data.avgAgeByCompany,
                          null,
                          2,
                          data.companyColors
                        )}
                      </>
                    ) : null}
                  </TabsCustomItem>

                  <TabsCustomItem active={this.state.tabIndex === 1}>
                    {this.state.tabIndex === 1 ? (
                      <>
                        {this.renderGroupChart(
                          "Encours impayé à fin de mois (€) — groupe",
                          data.labels,
                          data.amountGroup,
                          "currency",
                          2
                        )}
                        {this.renderGroupChart(
                          "Dossiers impayés à fin de mois — groupe",
                          data.labels,
                          data.countGroup,
                          null,
                          0
                        )}
                        {this.renderGroupChart(
                          "Âge moyen de l'encours (jours) — groupe",
                          data.labels,
                          data.avgAgeGroup,
                          null,
                          2
                        )}
                      </>
                    ) : null}
                  </TabsCustomItem>

                  <TabsCustomItem active={this.state.tabIndex === 2}>
                    {this.state.tabIndex === 2 ? (
                      <>
                        {this.renderMultiChart(
                          "Encours impayé à fin de mois (€) — par région",
                          data.labels,
                          data.amountByRegion,
                          "currency",
                          2
                        )}
                        {this.renderMultiChart(
                          "Dossiers impayés à fin de mois — par région",
                          data.labels,
                          data.countByRegion,
                          null,
                          0
                        )}
                        {this.renderMultiChart(
                          "Âge moyen de l'encours (jours) — par région",
                          data.labels,
                          data.avgAgeByRegion,
                          null,
                          2
                        )}
                      </>
                    ) : null}
                  </TabsCustomItem>

                  <TabsCustomItem active={this.state.tabIndex === 3}>
                    {this.state.tabIndex === 3 ? (
                      <>
                        {this.renderMultiChart(
                          "Encours impayé à fin de mois (€) — par agence",
                          data.labels,
                          data.amountByAgency,
                          "currency",
                          2,
                          data.agencyColors
                        )}
                        {this.renderMultiChart(
                          "Dossiers impayés à fin de mois — par agence",
                          data.labels,
                          data.countByAgency,
                          null,
                          0,
                          data.agencyColors
                        )}
                        {this.renderMultiChart(
                          "Âge moyen de l'encours (jours) — par agence",
                          data.labels,
                          data.avgAgeByAgency,
                          null,
                          2,
                          data.agencyColors
                        )}
                      </>
                    ) : null}
                  </TabsCustomItem>
                </TabsCustomBody>
              </TabsCustom>

              <Card
                className="mb-10 mb-20-xl"
                title={`Factures impayées à date (${data.summary.totalInvoices})`}
                action={
                  <FontAwesomeIcon
                    icon={faDownload}
                    style={{ cursor: "pointer" }}
                    onClick={this.downloadCSV}
                  />
                }
              >
                {data.summary.totalInvoices > data.folders.length ? (
                  <p className="text-muted mb-10">
                    Aperçu des {data.folders.length} factures de plus gros
                    montant (sur {data.summary.totalInvoices}). Export CSV = liste
                    complète.
                  </p>
                ) : null}
                <Table>
                  <Thead>
                    <Tr>
                      <Th>Référence</Th>
                      <Th>N° facture</Th>
                      <Th>Compagnie</Th>
                      <Th>Région</Th>
                      <Th>Agence</Th>
                      <Th>Date facture</Th>
                      <Th>Montant HT</Th>
                      <Th>Montant TTC</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {data.folders.map((f, idx) => (
                      <Tr key={idx}>
                        <Td>{f.folderRef}</Td>
                        <Td>{f.invoice}</Td>
                        <Td>{f.company}</Td>
                        <Td>{f.region}</Td>
                        <Td>{f.agency}</Td>
                        <Td>{f.date}</Td>
                        <Td>{getCurrencyValue(f.amountHt)}</Td>
                        <Td>{getCurrencyValue(f.amountTtc)}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Card>
            </>
          ) : null}
        </FadeIn>
        <Loader show={this.state.isLoad} />
      </>
    );
  }
}
